import { IonListHeader } from '@ionic/react';
import { useFormikContext } from 'formik';
import { cloneDeep, get, has } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import * as yup from 'yup';
import { OnFormChange } from '../../../../../../components/form/form-change';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { isObject } from '../../../../../../util/object';
import { FlowItemEditCommonProps } from '../../../../shared/component/flow-item/edit/flow-item-edit.types';
import {
  ParameterListExtensions,
  ParametersList,
} from '../../../../shared/component/flow-item-parameter/parameter-list';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItemForm,
  FlowItemParameterMetadata,
  ParametersFormValue,
} from '../../../../shared/model/flow.types';
import { GetYupSchemaForItemContext } from '../../../../shared/service/service.types';
import { instanceOfFlowItemSection } from '../../../../shared/util/flow-item.util';
import { useWeldParameterMessages } from '../../hooks/use-weld-parameter-messages';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { getWeldingParameterMetadataFromForm } from '../../util/beacon-welding.util';

export interface WeldProcessSettingsFormProps
  extends Omit<FlowItemEditCommonProps, 'item'> {
  readOnly?: boolean;
  extensions?: ParameterListExtensions;
  parameterPermissions?: Record<string, boolean>;
  parameterOptions?: BeaconWeldingData.GetParameterMetadataFromFormOptions;
  setValidationSchema: (schema: yup.AnySchema | undefined) => void;
  validationSchemaContext?: GetYupSchemaForItemContext;
  formikValuePath?: string;
  parameterListPrefix?: string;
  recalculateParameterValues?: (form: FlowItemForm) => FlowItemForm;
}

const WeldProcessSettingsFormHeader = (props: {
  header?: string;
  actionButton?: ReactElement;
}): ReactElement => {
  const { header, actionButton } = props;

  return (
    <div className="configuration-header">
      <IonListHeader
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {header ?? getLocalizedText('welding:library_parameters_header')}
        </span>
        {actionButton && (
          <div style={{ marginLeft: 'auto' }}>{actionButton}</div>
        )}
      </IonListHeader>
    </div>
  );
};

const WeldProcessSettingsFormWithHeader = (
  props: WeldProcessSettingsFormProps & {
    header?: string;
    actionButton?: ReactElement;
  }
): ReactElement => {
  const { header, actionButton, ...weldProcessSettingsProps } = props;

  return (
    <>
      <WeldProcessSettingsFormHeader
        header={header}
        actionButton={actionButton}
      />
      <WeldProcessSettingsForm {...weldProcessSettingsProps} />
    </>
  );
};

const WeldProcessSettingsForm = (
  props: WeldProcessSettingsFormProps
): ReactElement | null => {
  const {
    readOnly,
    system,
    setValidationSchema,
    formikValuePath,
    parameterListPrefix,
    parameterOptions,
    extensions,
    parameterPermissions,
    recalculateParameterValues,
    validationSchemaContext,
  } = props;

  const { flow, flowItemService } =
    useFlowServices<BeaconWeldingData.FlowConfig>();
  const [parameters, setParameters] = useState<FlowItemParameterMetadata[]>([]);
  const { values, setFieldValue } = useFormikContext<FlowItemForm>();

  const [parameterFormOptions, setParameterFormOptions] =
    useState<BeaconWeldingData.GetParameterMetadataFromFormOptions>(
      parameterOptions ?? {}
    );

  const parameterMessageInfo = useWeldParameterMessages(
    values,
    formikValuePath
  );

  useDeepCompareEffect(() => {
    setParameterFormOptions({
      ...parameterOptions,
    });
  }, [parameterOptions ?? {}]);

  const onChangeForm = useCallback(
    (form: FlowItemForm) => {
      let actualForm: FlowItemForm = formikValuePath
        ? get(form, formikValuePath)
        : form;

      if (recalculateParameterValues) {
        const recalculatedForm = recalculateParameterValues(actualForm);

        // Apply the recalculated parameter values back to the Formik form
        if (recalculatedForm.parameters) {
          Object.entries(recalculatedForm.parameters).forEach(
            ([ref, paramValue]) => {
              // Only update if the parameter value has actually changed
              const currentValue = actualForm.parameters[ref]?.value;
              if (currentValue !== paramValue.value) {
                const fieldPath = formikValuePath
                  ? `${formikValuePath}.parameters.${ref}.value`
                  : `parameters.${ref}.value`;
                setFieldValue(fieldPath, paramValue.value, false);
              }
            }
          );
        }

        actualForm = recalculatedForm;
      }

      const params = getWeldingParameterMetadataFromForm(
        flowItemService,
        actualForm.parameters,
        parameterFormOptions
      );

      const schema = flowItemService.getYupSchemaForItem(
        BeaconWeldingData.FlowItemRef.WELD,
        props.system,
        validationSchemaContext ?? { flow, form: actualForm },
        params
      );

      setParameters(params);
      setValidationSchema(schema);
    },
    [
      formikValuePath,
      flowItemService,
      parameterFormOptions,
      props.system,
      setValidationSchema,
      recalculateParameterValues,
      flow,
      validationSchemaContext,
      setFieldValue,
    ]
  );

  const formAsItem = useMemo(() => {
    return flowItemService.convertFormToFlowItem(
      formikValuePath ? get(values, formikValuePath) : values,
      props.system
    );
  }, [flowItemService, formikValuePath, props.system, values]);

  const modifiedParameterPermissions = useMemo(() => {
    if (!parameterPermissions) {
      return;
    }

    const actualForm: FlowItemForm = formikValuePath
      ? get(values, formikValuePath)
      : values;

    if (instanceOfFlowItemSection(formAsItem)) {
      return;
    }

    const parametersMetadata = getWeldingParameterMetadataFromForm(
      flowItemService,
      actualForm.parameters,
      parameterFormOptions
    );

    return getValidParameterPermissions({
      parameterPermissions,
      parametersMetadata,
      form: actualForm,
    });
  }, [
    flowItemService,
    formAsItem,
    formikValuePath,
    parameterFormOptions,
    parameterPermissions,
    values,
  ]);

  return (
    <>
      <OnFormChange onFormChange={onChangeForm} />
      <ParametersList
        extensions={extensions}
        parameters={parameters}
        parameterPermissions={modifiedParameterPermissions}
        parametersRecommendationInfo={parameterMessageInfo}
        system={system}
        readOnly={readOnly}
        formPrefix={parameterListPrefix}
      />
    </>
  );
};

// This function sets the parameter permissions to false if the form parameters
// do not match the expected type as defined in the metadata. This is only
// relevant for custom permissions and is a fall back to handle bad data.
const getValidParameterPermissions = (params: {
  parameterPermissions: Record<string, boolean>;
  parametersMetadata: FlowItemParameterMetadata[];
  form: FlowItemForm;
}): Record<string, boolean> => {
  const { parameterPermissions, parametersMetadata, form } = params;
  const modifiedParameterPermissions = cloneDeep(parameterPermissions);

  for (const metadata of parametersMetadata) {
    if (!has(form.parameters, metadata.ref)) {
      continue;
    }

    const parameterFormValue = form.parameters[metadata.ref];

    const isValid = isOriginalBaseValueValid({
      metadata,
      parameterFormValue,
    });

    if (!isValid) {
      console.warn(
        `Parameter ${metadata.ref} has a metadata type of ${
          metadata.value.type
        } but the form value is of type ${typeof parameterFormValue.originalBaseValue}.`,
        { parameterFormValue, metadata }
      );
      modifiedParameterPermissions[metadata.ref] = false;
    }
  }

  return modifiedParameterPermissions;
};

const isOriginalBaseValueValid = (params: {
  metadata: FlowItemParameterMetadata;
  parameterFormValue: ParametersFormValue;
}): boolean => {
  const { metadata, parameterFormValue } = params;
  if (metadata.value.type === 'array') {
    return Array.isArray(parameterFormValue.originalBaseValue);
  } else if (metadata.value.type === 'object') {
    return isObject(parameterFormValue.originalBaseValue);
  }

  return typeof parameterFormValue.originalBaseValue === metadata.value.type;
};

export {
  WeldProcessSettingsForm,
  WeldProcessSettingsFormHeader,
  WeldProcessSettingsFormWithHeader,
};
